import { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { EnvSchema } from '../EnvSelector';
import { callEdgeFunction } from '../supabase.client';

interface Stage {
  id: number;
  world_id: number;
  bg: string | null;
  name: string;
  total_waves: number;
  recommend_combat_point: number;
  map_id: string | null;
}

interface World {
  id: number;
  name: string;
}

interface WaveCount {
  waves: number;
  enemies: number;
}

interface ListResp<T> {
  rows: T[];
}

interface Props {
  env: EnvSchema;
}

export function StageView({ env }: Props) {
  const [stages, setStages] = useState<Stage[]>([]);
  const [worlds, setWorlds] = useState<Map<number, World>>(new Map());
  const [counts, setCounts] = useState<Record<number, WaveCount>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const [s, w, sw, swe] = await Promise.all([
          callEdgeFunction<ListResp<Stage>>('crud', {
            schema: env,
            table: 'stage',
            op: 'list',
            limit: 1000,
          }),
          callEdgeFunction<ListResp<World>>('crud', {
            schema: env,
            table: 'world',
            op: 'list',
            limit: 1000,
          }),
          callEdgeFunction<ListResp<{ stage_id: number }>>('crud', {
            schema: env,
            table: 'stage_wave',
            op: 'list',
            limit: 5000,
          }),
          callEdgeFunction<ListResp<{ stage_id: number }>>('crud', {
            schema: env,
            table: 'stage_wave_enemy',
            op: 'list',
            limit: 20000,
          }),
        ]);
        setStages(s.rows);
        setWorlds(new Map(w.rows.map((r) => [r.id, r])));
        const c: Record<number, WaveCount> = {};
        for (const row of sw.rows) {
          const id = row.stage_id;
          c[id] ??= { waves: 0, enemies: 0 };
          c[id].waves++;
        }
        for (const row of swe.rows) {
          const id = row.stage_id;
          c[id] ??= { waves: 0, enemies: 0 };
          c[id].enemies++;
        }
        setCounts(c);
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [env]);

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading stages…</div>;
  }
  if (err) {
    return <div className="text-sm text-rose-600 dark:text-rose-400">Error: {err}</div>;
  }

  return (
    <div className="flex flex-col gap-2">
      <div>
        <h2 className="text-base font-medium text-foreground">
          {env} / <span className="font-mono">stage</span>{' '}
          <span className="text-xs text-muted-foreground">(custom view)</span>
        </h2>
        <p className="text-xs text-muted-foreground">
          Aggregated view. For raw row editing, use the generic editor by removing the stage entry
          from the view registry.
        </p>
      </div>
      <div className="max-h-[calc(100vh-200px)] overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader className="bg-muted/40">
            <TableRow>
              <TableHead className="text-xs">id</TableHead>
              <TableHead className="text-xs">name</TableHead>
              <TableHead className="text-xs">world</TableHead>
              <TableHead className="text-xs">bg</TableHead>
              <TableHead className="text-xs">waves</TableHead>
              <TableHead className="text-xs">enemy spawns</TableHead>
              <TableHead className="text-xs">map_id</TableHead>
              <TableHead className="text-xs">recommend CP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {stages
              .slice()
              .sort((a, b) => a.id - b.id)
              .map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-mono text-xs">{s.id}</TableCell>
                  <TableCell className="text-xs">{s.name}</TableCell>
                  <TableCell className="text-xs">
                    {worlds.get(s.world_id)?.name ?? `#${s.world_id}`}
                  </TableCell>
                  <TableCell className="text-xs opacity-70">{s.bg ?? '—'}</TableCell>
                  <TableCell className="text-xs">
                    {counts[s.id]?.waves ?? 0} / {s.total_waves}
                  </TableCell>
                  <TableCell className="text-xs">{counts[s.id]?.enemies ?? 0}</TableCell>
                  <TableCell className="font-mono text-xs">{s.map_id ?? ''}</TableCell>
                  <TableCell className="text-xs">{s.recommend_combat_point}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
